import { faTableColumns } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon } from '@mantine/core';
import { RowData, Table } from '@tanstack/react-table';
import { PopoverSelect } from '@vision/ui/components/PopoverSelect';
import { KeyValue } from '@vision/ui/interfaces';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const EXCLUDED_COLUMN_IDS = ['data-grid-selection', 'data-grid-action-menu'];

interface DataGridColumnVisibilitySelectorProps<TData extends RowData> {
  table: Table<TData>;
}

function _DataGridColumnVisibilitySelector<TData extends RowData>({
  table,
}: DataGridColumnVisibilitySelectorProps<TData>) {
  const { t } = useTranslation('data-grid');
  const [opened, setOpened] = useState(false);

  const selectableColumns: KeyValue[] = table
    .getAllLeafColumns()
    .filter((column) => !EXCLUDED_COLUMN_IDS.includes(column.id))
    .map((column) => {
      const header = column.columnDef.header;
      return {
        key: column.id,
        value: typeof header === 'string' ? header : column.id,
      };
    });

  const selectedColumns = selectableColumns.filter((item) => table.getColumn(item.key)?.getIsVisible());

  const handleColumnSelection = (item: KeyValue) => {
    const column = table.getColumn(item.key);
    if (!column) {
      return;
    }

    column.toggleVisibility(!column.getIsVisible());
    table.resetColumnSizing();
    table.resetHeaderSizeInfo();
  };

  const handleToggle = () => {
    setOpened((prev) => !prev);
  };

  return (
    <PopoverSelect
      icon={faTableColumns}
      items={selectableColumns}
      offset={8}
      opened={opened}
      onDismiss={() => setOpened(false)}
      onSelect={handleColumnSelection}
      position="bottom-end"
      title={t('configureColumns')}
      value={selectedColumns}
      withSelectAll={false}
      maxDropdownHeight={250}
    >
      <ActionIcon
        data-testid="button-data-grid-column-selector"
        variant={opened ? 'filled' : 'light'}
        color={opened ? 'blue' : 'gray'}
        onClick={handleToggle}
        aria-label={t('configureColumns')}
      >
        <FontAwesomeIcon icon={faTableColumns} />
      </ActionIcon>
    </PopoverSelect>
  );
}

export const DataGridColumnVisibilitySelector = _DataGridColumnVisibilitySelector;
